/**
 * @file 手書きの道具の並びである (ペンの色・太さ・消しゴム・取り消し / やり直し / 全消し)。
 *
 * @what: 状態は親が持ち、ここは押しボタンを並べるだけにする。メモの手書きのモーダルと、
 *   ページの上の書き込みで同じものを使う。「全消し」の隣には呼び出し側のボタンを置ける
 *   (afterClear)。全消しだけは確認のダイアログを一度はさむ
 *   (代表の判断、2026-09-09)。※なぜなら書いたものが一度に消え、やり直しで戻せることに
 *   気づきにくいためである。
 * @failure: 描画のみ。
 */

import { useState, type ReactNode } from "react";
import { PEN_COLORS, PEN_SIZES, type HandwritingTool } from "../model/handwriting";
import { StudyDialog } from "./StudyDialog";

export function HandwritingToolbar({
  tool,
  colorId,
  sizeId,
  onTool,
  onColor,
  onSize,
  canUndo,
  canRedo,
  canClear,
  onUndo,
  onRedo,
  onClear,
  afterClear,
}: {
  tool: HandwritingTool;
  colorId: string;
  sizeId: string;
  onTool: (tool: HandwritingTool) => void;
  onColor: (colorId: string) => void;
  onSize: (sizeId: string) => void;
  canUndo: boolean;
  canRedo: boolean;
  canClear: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onClear: () => void;
  /** 「全消し」の隣に置くもの (ページの書き込みの「もう一度解く」)。 */
  afterClear?: ReactNode;
}) {
  const [confirmingClear, setConfirmingClear] = useState(false);
  return (
    <div className="study-hw-toolbar" role="toolbar" aria-label="手書きの道具">
      <span className="study-hw-group" role="group" aria-label="ペン">
        {PEN_COLORS.map((c) => (
          <button
            key={c.id}
            type="button"
            className="study-hw-tool"
            aria-pressed={tool === "pen" && colorId === c.id}
            onClick={() => {
              onTool("pen");
              onColor(c.id);
            }}
          >
            <span className="study-hw-swatch" style={{ background: c.color }} aria-hidden="true" />
            {c.label}
          </button>
        ))}
        <button
          type="button"
          className="study-hw-tool"
          aria-pressed={tool === "eraser"}
          onClick={() => onTool("eraser")}
        >
          消しゴム
        </button>
      </span>
      <span className="study-hw-group" role="group" aria-label="太さ">
        {PEN_SIZES.map((s) => (
          <button
            key={s.id}
            type="button"
            className="study-hw-tool"
            aria-pressed={sizeId === s.id}
            onClick={() => onSize(s.id)}
          >
            {s.label}
          </button>
        ))}
      </span>
      <span className="study-hw-group" role="group" aria-label="もどす">
        <button type="button" className="study-hw-tool" onClick={onUndo} disabled={!canUndo}>
          取り消し
        </button>
        <button type="button" className="study-hw-tool" onClick={onRedo} disabled={!canRedo}>
          やり直し
        </button>
        <button
          type="button"
          className="study-hw-tool"
          onClick={() => setConfirmingClear(true)}
          disabled={!canClear}
        >
          全消し
        </button>
        {afterClear}
      </span>
      {confirmingClear ? (
        <StudyDialog
          title="書いたものを全部消しますか"
          labelledBy="study-hw-clear-title"
          onClose={() => setConfirmingClear(false)}
        >
          <p>消したあとも「やり直し」で戻せます。</p>
          <p className="study-ask-dialog-actions">
            <button
              type="button"
              className="study-ghost-btn"
              onClick={() => setConfirmingClear(false)}
            >
              やめる
            </button>
            <button
              type="button"
              className="study-danger-btn"
              onClick={() => {
                setConfirmingClear(false);
                onClear();
              }}
            >
              全部消す
            </button>
          </p>
        </StudyDialog>
      ) : null}
    </div>
  );
}
